import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { MapPin, ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ListingCard from '@/components/ListingCard';
import Map from '@/components/Map';
import { BreederAnnouncements } from '@/components/BreederAnnouncements';
import { MessageBreederButton } from '@/components/messaging/MessageBreederButton';
import { useBreederListings } from '@/hooks/useBreederListings';
import { useBreederProfile } from '@/hooks/useBreederProfile';
import { useBreederAnnouncements } from '@/hooks/useBreederAnnouncements';

const BreederProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // All hooks must be called at the top, before any conditional logic
  const { data: profile, isLoading: profileLoading, error: profileError } = useBreederProfile({ 
    breederId: id || '' 
  });

  const { data: listingsData, isLoading: listingsLoading, error: listingsError } = useBreederListings({ 
    breederId: id || '' 
  });

  const { data: announcements, isLoading: announcementsLoading, error: announcementsError } = useBreederAnnouncements({ 
    breederId: id || '' 
  });

  // Create breeder location data for the map
  const breederMapData = React.useMemo(() => {
    if (!profile) return null;

    // Use real coordinates if available, otherwise use default coordinates
    let lat = 40.7128; // Default NYC lat
    let lng = -74.0060; // Default NYC lng

    if (profile.locationCoordinates) {
      console.log('Raw locationCoordinates from database:', profile.locationCoordinates);
      
      try {
        let coordinates;
        
        // If it's a string, parse it as JSON
        if (typeof profile.locationCoordinates === 'string') {
          coordinates = JSON.parse(profile.locationCoordinates);
        } else {
          coordinates = profile.locationCoordinates;
        }
        
        console.log('Parsed coordinates:', coordinates);
        
        // Check if coordinates has longitude and latitude properties
        if (coordinates && typeof coordinates === 'object' && 
            'longitude' in coordinates && 'latitude' in coordinates &&
            typeof coordinates.longitude === 'number' && 
            typeof coordinates.latitude === 'number') {
          lat = coordinates.latitude;
          lng = coordinates.longitude;
          console.log('Successfully extracted coordinates:', { lat, lng });
        } else {
          console.log('Invalid coordinate format, using default coordinates');
        }
      } catch (error) {
        console.log('Error parsing coordinates:', error);
        console.log('Using default coordinates');
      }
    } else {
      console.log('No coordinates found in database, using default coordinates');
    }

    const mapData = [{
      id: 1,
      name: profile.name,
      location: profile.location,
      lat: lat,
      lng: lng
    }];
    
    console.log('Final breeder map data:', mapData);
    return mapData;
  }, [profile]);
  
  const bulls = listingsData?.bulls || [];
  const cows = listingsData?.cows || [];
  const totalListings = bulls.length + cows.length;

  // Get first letter of breeder name for profile image fallback
  const getInitial = (name: string) => {
    return name ? name.charAt(0).toUpperCase() : 'B';
  };

  const handleGoBack = () => {
    // Go back to previous page, or fallback to marketplace if no history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/marketplace');
    }
  };

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Invalid breeder profile ID.</p>
            <button 
              onClick={() => navigate('/marketplace')}
              className="text-green-700 hover:text-green-800 mt-4 inline-block cursor-pointer"
            >
              Back to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (profileLoading || listingsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (profileError || listingsError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Unable to load breeder profile at the moment.</p>
            <p className="text-sm text-gray-500 mt-2">This might be because the breeder ID is invalid or the breeder doesn't exist.</p>
            <button 
              onClick={() => navigate('/marketplace')}
              className="text-green-700 hover:text-green-800 mt-4 inline-block cursor-pointer"
            >
              Back to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button 
          onClick={handleGoBack}
          className="inline-flex items-center text-green-700 hover:text-green-800 mb-6 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Profile Header with Cover and Profile Image */}
        <div className="bg-white rounded-lg shadow-sm mb-8 overflow-hidden">
          {/* Cover Image Section */}
          <div className="relative">
            <AspectRatio ratio={4/1}>
              {profile?.coverImage ? (
                <img 
                  src={profile.coverImage} 
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-400 via-green-500 to-green-600" />
              )}
            </AspectRatio>
            
            {/* Profile Image - positioned to overlap cover image boundary */}
            <div className="absolute -bottom-12 left-6 z-30">
              <div className="relative">
                <div className="w-20 h-20 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg overflow-hidden">
                  {profile?.profileImage ? (
                    <img 
                      src={profile.profileImage} 
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-green-500 flex items-center justify-center">
                      <span className="text-white text-4xl font-bold">
                        {getInitial(profile?.name || '')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-20 px-8 pb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{profile?.name}</h1>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{profile?.location}</span>
                </div>
              </div>
              {profile && (
                <MessageBreederButton
                  listingId={`profile-${id}`}
                  breederId={id}
                  listingTitle={`Message ${profile.name}`}
                  listingUrl={window.location.href}
                  className="bg-green-700 hover:bg-green-800"
                />
              )}
            </div>

            {/* Breeds badges */}
            {profile?.breeds && profile.breeds.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.breeds.map((breed, index) => (
                  <Badge key={index} className="bg-green-100 text-sm text-green-800 hover:bg-green-200">
                    {breed}
                  </Badge>
                ))}
              </div>
            )}

            <p className="text-gray-700 leading-relaxed mb-4">{profile?.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <div className="text-sm text-gray-600">Total Listings</div>
                <div className="font-medium">{totalListings}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Announcements Section - Only show if announcements exist */}
        {announcements && announcements.length > 0 && (
          <div className="mb-8">
            <BreederAnnouncements 
              announcements={announcements} 
              loading={announcementsLoading} 
            />
          </div>
        )}

        {/* Bulls Section - Only show if bulls exist */}
        {bulls.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Bulls</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {bulls.map((bull) => (
                <ListingCard key={bull.id} listing={bull} />
              ))}
            </div>
          </div>
        )}

        {/* Cows Section - Only show if cows exist */}
        {cows.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Cows</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {cows.map((cow) => (
                <ListingCard key={cow.id} listing={cow} />
              ))}
            </div>
          </div>
        )}

        {/* Map Section - Only render when profile has location data */}
        {profile && profile.location && breederMapData && (
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Location</h2>
            <Map 
              breeders={breederMapData} 
              center={[breederMapData[0].lng, breederMapData[0].lat]} 
              zoom={10} 
              height="400px" 
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BreederProfile;
